import { useEffect, useState } from "react";
import { axiosInstance } from "contexts/axiosApiInstance";
import { ResourceType } from "./entities";
import type { DirectoryEntry } from "./EmailChipInput";
import { createLogger } from "debug/DebugingLogger";

const log = createLogger("useShareScope");

export interface ShareScope {
  scope: "open" | "personal" | "company";
  orgName?: string;
  /// The org roster, company scope only — the universe the share dialog
  /// restricts entry to.
  directory?: DirectoryEntry[];
}

interface OrgMemberResponse {
  email: string;
  display_name: string;
}

/// Which world a table's share dialog lives in. A company org's table is
/// shareable to its members only (D19), so the dialog swaps free text for the
/// roster; everything else keeps free entry. Advisory: on any failure the
/// scope stays "open" and the share door remains the authority.
export const useShareScope = (
  entityId: string,
  resourceType: ResourceType,
): ShareScope => {
  const [scope, setScope] = useState<ShareScope>({ scope: "open" });

  useEffect(() => {
    // Tables only for now — the other doors keep free text until they grow a
    // scope read of their own.
    if (resourceType !== ResourceType.Table || !entityId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const { data } = await axiosInstance.get<{
          scope: "open" | "personal" | "company";
          org_id?: string;
          org_name?: string;
        }>(`/action/tables/${entityId}/share_scope`);
        if (cancelled) return;
        if (data.scope !== "company" || !data.org_id) {
          setScope({ scope: data.scope });
          return;
        }
        const { data: members } = await axiosInstance.get<OrgMemberResponse[]>(
          `/action/organizations/${data.org_id}/members`,
        );
        if (cancelled) return;
        setScope({
          scope: "company",
          orgName: data.org_name,
          directory: members
            .filter((m) => m.email)
            .map((m) => ({
              email: m.email,
              displayName: m.display_name || undefined,
            })),
        });
      } catch (e) {
        log.error("share scope lookup failed, keeping free entry:", e);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [entityId, resourceType]);

  return scope;
};
